import React, { Fragment, useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import {
  Button, Col, DropdownItem, DropdownMenu,
  DropdownToggle, Media, Row, UncontrolledDropdown, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input
} from 'reactstrap';
import { MoreVertical, PlusCircle, ToggleLeft, ToggleRight, Trash2, Edit, Folder } from 'react-feather';
import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';
import { baseURL } from '../../../../Services/api/baseURL';
import Loader from '../../../Loader/Loader';

import useRbac from '../../../../Hooks/useRbac';

const CategoryManagement = () => {
  const userRole = JSON.parse(localStorage.getItem('role_name'));
  const { can, canAny } = useRbac();
  const token = JSON.parse(localStorage.getItem("token"));

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryData, setCategoryData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    totalRows: 0
  });
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    metaTitle: '',
    metaDescription: '',
    order: 0
  });

  // Fetch categories
  const fetchCategories = async (page) => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseURL}/api/categories/admin/all`, {
        params: {
          page,
          limit: pagination.perPage,
          includeInactive: true
        },
        headers: {
          Authorization: ` ${token}`,
        }
      });

      const { categories, total, totalPages } = response.data;
      setCategoryData(categories || []);
      setPagination(prev => ({
        ...prev,
        totalRows: total,
        totalPages: totalPages
      }));
    } catch (error) {
      console.error('Error fetching categories:', error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch categories",
        icon: "error",
        confirmButtonColor: "#fc2c54",
      });
    } finally {
      setLoading(false);
    }
  };

  // Toggle category status
  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await axios.patch(
        `${baseURL}/api/categories/${id}/toggle`,
        {},
        {
          headers: {
            Authorization: ` ${token}`,
          }
        }
      );

      Swal.fire({
        title: currentStatus ? "Category Deactivated!" : "Category Activated!",
        icon: "success",
        confirmButtonColor: "#fc2c54",
        timer: 2000,
        showConfirmButton: false
      });

      fetchCategories(pagination.page);
    } catch (error) {
      console.error('Error toggling category status:', error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "Failed to update category status",
        icon: "error",
        confirmButtonColor: "#fc2c54",
      });
    }
  };

  // Delete category
  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "This will delete the category. Blogs in this category won't be deleted but will be uncategorized.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#D31789",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Yes, delete it!"
      });

      if (result.isConfirmed) {
        await axios.delete(`${baseURL}/api/categories/${id}`, {
          headers: {
            Authorization: ` ${token}`,
          }
        });

        Swal.fire({
          title: "Deleted!",
          text: "Category has been deleted successfully.",
          icon: "success",
          confirmButtonColor: "#fc2c54",
        });

        fetchCategories(pagination.page);
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "Failed to delete category",
        icon: "error",
        confirmButtonColor: "#fc2c54",
      });
    }
  };

  // Open modal for create/edit
  const openModal = (category = null) => {
    if (category) {
      setIsEditing(true);
      setCurrentCategory(category);
      setFormData({
        name: category.name || '',
        description: category.description || '',
        metaTitle: category.metaTitle || '',
        metaDescription: category.metaDescription || '',
        order: category.order || 0
      });
    } else {
      setIsEditing(false);
      setCurrentCategory(null);
      setFormData({
        name: '',
        description: '',
        metaTitle: '',
        metaDescription: '',
        order: 0
      });
    }
    setShowModal(true);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Submit form
  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      Swal.fire({
        title: "Error",
        text: "Category name is required",
        icon: "error",
        confirmButtonColor: "#fc2c54",
      });
      return;
    }

    try {
      if (isEditing) {
        await axios.put(
          `${baseURL}/api/categories/${currentCategory._id}`,
          formData,
          {
            headers: {
              Authorization: ` ${token}`,
            }
          }
        );
        Swal.fire({
          title: "Updated!",
          text: "Category has been updated successfully.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        await axios.post(
          `${baseURL}/api/categories`,
          formData,
          {
            headers: {
              Authorization: ` ${token}`,
            }
          }
        );
        Swal.fire({
          title: "Created!",
          text: "Category has been created successfully.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });
      }

      setShowModal(false);
      fetchCategories(pagination.page);
    } catch (error) {
      console.error('Error saving category:', error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "Failed to save category",
        icon: "error",
        confirmButtonColor: "#fc2c54",
      });
    }
  };

  // Table columns
  const columns = [
    {
      name: 'Category Name',
      selector: row => row.name,
      sortable: true,
      cell: row => (
        <div style={{ padding: '8px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Folder size={16} className="text-primary" />
            <span style={{ fontWeight: '600', color: '#2c3e50' }}>{row.name}</span>
          </div>
          {row.description && (
            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
              {row.description.length > 60 ? row.description.substring(0, 60) + '...' : row.description}
            </div>
          )}
        </div>
      ),
      grow: 2
    },
    {
      name: 'Slug',
      selector: row => row.slug,
      sortable: true,
      cell: row => (
        <code style={{ fontSize: '12px', backgroundColor: '#f8f9fa', padding: '2px 6px', borderRadius: '4px' }}>
          {row.slug}
        </code>
      ),
      width: '180px'
    },
    {
      name: 'Blogs',
      selector: row => row.blogCount,
      sortable: true,
      center: true,
      cell: row => (
        <Badge color="info" style={{ fontSize: '11px' }}>
          {row.blogCount || 0} blogs
        </Badge>
      ),
      width: '100px'
    },
    {
      name: 'Order',
      selector: row => row.order,
      sortable: true,
      center: true,
      width: '80px'
    },
    {
      name: 'Status',
      selector: row => row.isActive,
      sortable: true,
      center: true,
      cell: row => (
        <Badge color={row.isActive ? 'success' : 'danger'} style={{ fontSize: '10px', padding: '4px 8px' }}>
          {row.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
      width: '100px'
    },
    {
      name: 'Created',
      selector: row => row.createdAt,
      sortable: true,
      center: true,
      cell: row => (
        <span style={{ fontSize: '12px', color: '#6c757d' }}>
          {moment(row.createdAt).format("MMM DD, YYYY")}
        </span>
      ),
      width: '120px'
    },
    {
      name: 'Actions',
      omit: !canAny(['blog_categories.update', 'blog_categories.delete', 'blog_categories.change_status']),
      cell: row => (
        <UncontrolledDropdown>
          <DropdownToggle tag="span" className="p-2 cursor-pointer">
            <MoreVertical size={14} />
          </DropdownToggle>
          <DropdownMenu>
            {can('blog_categories.update') && <DropdownItem
              onClick={() => openModal(row)}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 12px' }}
            >
              <Edit size={12} className="me-2" /> Edit
            </DropdownItem>}
            {can('blog_categories.update') && <DropdownItem
              onClick={() => handleToggleStatus(row._id, row.isActive)}
              className={row.isActive ? "text-danger" : "text-success"}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 12px' }}
            >
              {row.isActive ? (
                <>
                  <ToggleLeft size={12} className="me-2" /> Deactivate
                </>
              ) : (
                <>
                  <ToggleRight size={12} className="me-2" /> Activate
                </>
              )}
            </DropdownItem>}
            {can('blog_categories.delete') && <DropdownItem
              onClick={() => handleDelete(row._id)}
              className="text-danger"
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 12px' }}
            >
              <Trash2 size={12} className="me-2" /> Delete
            </DropdownItem>}
          </DropdownMenu>
        </UncontrolledDropdown>
      ),
      sortable: false,
      center: true,
      width: '80px'
    }
  ];

  // Effects
  useEffect(() => {
    fetchCategories(1);
  }, []);

  const filteredCategories = categoryData.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.slug && item.slug.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Fragment>
      <Row className='pb-4 p-6'>
        <div className='d-flex justify-content-between align-items-center'>
          <div>
            {/* <H4>Category Management</H4> */}
              <h5 className="fw-bolder">Category Management</h5>
            <p className="text-muted mb-0">Manage your blog categories</p>
          </div>
          <div className="file-content">
            <Media>
              <div className='mb-0 form-group position-relative search_outer d-flex align-items-center'>
                <i className='fa fa-search'></i>
                <input
                  className='form-control border-0'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  type='text'
                  placeholder='Search categories...'
                  style={{ paddingLeft: '35px' }}
                />
              </div>
              {can('blog_categories.create') && (
                <Media body className='text-end ms-3'>
                  <Button className='btn btn-primary d-flex align-items-center' onClick={() => openModal()}>
                    <PlusCircle size={16} className="me-2" />
                    Create Category
                  </Button>
                </Media>
              )}
            </Media>
          </div>
        </div>
      </Row>

      <DataTable
        data={filteredCategories}
        columns={columns}
        pagination
        paginationServer
        paginationTotalRows={pagination.totalRows}
        onChangePage={(page) => {
          setPagination(prev => ({ ...prev, page }));
          fetchCategories(page);
        }}
        onChangeRowsPerPage={(newPerPage, page) => {
          setPagination(prev => ({ ...prev, perPage: newPerPage, page }));
          fetchCategories(page);
        }}
        progressPending={loading}
        progressComponent={<Loader />}
        customStyles={{
          rows: {
            style: {
              minHeight: '60px',
              '&:hover': {
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
              },
            },
          },
          headCells: {
            style: {
              paddingLeft: '8px',
              paddingRight: '8px',
              fontWeight: '600',
              fontSize: '13px',
              color: '#495057',
            },
          },
          cells: {
            style: {
              paddingLeft: '8px',
              paddingRight: '8px',
            },
          },
        }}
      />

      {/* Create/Edit Category Modal */}
      <Modal isOpen={showModal} toggle={() => setShowModal(false)} size="lg">
        <ModalHeader toggle={() => setShowModal(false)}>
          {isEditing ? 'Edit Category' : 'Create New Category'}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Category Name <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter category name"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  name="order"
                  value={formData.order}
                  onChange={handleInputChange}
                  placeholder="0"
                  min="0"
                />
                <small className="text-muted">Lower numbers appear first</small>
              </FormGroup>
            </Col>
          </Row>
          
          <FormGroup>
            <Label>Description</Label>
            <Input
              type="textarea"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter category description (optional)"
              rows="3"
            />
          </FormGroup>

          <h6 className="mt-4 mb-3">SEO Settings</h6>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Meta Title</Label>
                <Input
                  type="text"
                  name="metaTitle"
                  value={formData.metaTitle}
                  onChange={handleInputChange}
                  placeholder="SEO title (60 chars max)"
                  maxLength="60"
                />
                <small className="text-muted">{formData.metaTitle.length}/60</small>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Meta Description</Label>
                <Input
                  type="textarea"
                  name="metaDescription"
                  value={formData.metaDescription}
                  onChange={handleInputChange}
                  placeholder="SEO description (160 chars max)"
                  maxLength="160"
                  rows="2"
                />
                <small className="text-muted">{formData.metaDescription.length}/160</small>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit}>
            {isEditing ? 'Update Category' : 'Create Category'}
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default CategoryManagement;